import {
  Box,
  Alert,
  AlertDescription,
  AlertIcon,
  HStack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  TableContainer,
  Skeleton,
  Flex,
  Text,
  Tooltip,
  Divider,
  Checkbox,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { Bank } from '@finley-technologies/finley-types';
import {
  getAdvanceRate,
  getCollateralDescription,
  getPreAllocationLendableValue,
  getPreAllocationMarketValue,
  getAllocationRate,
  getCollateralAccountStats,
  getPostAllocationLendableValue,
} from '@finley-technologies/valley-bank-utils';
import type { ValleyBankCollateralAccount } from '@finley-technologies/valley-bank-utils';

import { colors } from '~/packages/common-frontend/colors';
import { ProgressAlert } from '~/packages/common-frontend/components/Alert';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from '~/packages/common-frontend/components/CsvXlsxDownloadSelect';
import { Icon } from '~/packages/common-frontend/components/icons';
import { TanStackPaginationControl } from '~/packages/common-frontend/components/TanStack';
import { ColumnDisplaySettingsButton } from '~/packages/common-frontend/components/TanStack/ColumnReordering';
import {
  useTableColumnPreferences,
  getColumnNames,
  createDefaultTableColumnConfiguration,
  TableColumnConfigurationKey,
} from '~/packages/common-frontend/hooks/useTableColumnPreferences';
import {
  frozenHeaderRowStyling,
  frozenMultipleRowCellStyling,
  frozenMultipleHeaderCellStyling,
} from '~/packages/common-frontend/styles';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import {
  useBaseFieldColumnDefinitions,
  isInitiallyVisible,
  isBaseFieldToRename,
  getHeader,
  ALL_BASE_FIELDS,
} from '~/valleyBank/domains/Collateral/hooks/useBaseFieldColumnDefinitions';
import type {
  HoldingsTableColumnDef,
  Collateral,
  Receivable,
} from '~/valleyBank/domains/Collateral/types';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';
import { useLoanAccountUrlParam } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';

const IGNORED_CUSTOMER_COLUMN_SET = new Set([
  'allocation',
  'postAllocationLendableValue',
]);

const TABLE_KEY_CUSTOMER: TableColumnConfigurationKey =
  'valleyBankSbl.customerHoldings';

const TABLE_KEY_LOANS: TableColumnConfigurationKey =
  'valleyBankSbl.loanAccountHoldings';

const TABLE_KEY_ASSIGNMENT: TableColumnConfigurationKey =
  'valleyBankSbl.assignmentHoldings';

const _BASE_DEFAULT_COLUMN_CONFIGS_BASEFIELDS = ALL_BASE_FIELDS.map((id) => {
  return {
    columnKey: id,
    isVisible: false,
    isFixed: false,
  };
});

const _BASE_DEFAULT_COLUMN_CONFIGS_LOANS = [
  {
    columnKey: 'accountNumber',
    isVisible: true,
    isFixed: true,
  },
  {
    columnKey: 'product_name',
    isVisible: true,
    isFixed: true,
  },
  {
    columnKey: 'baseField-he_sec_sec_desc-securities',
    isVisible: true,
    isFixed: true,
  },
  {
    columnKey: 'marketValue',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'advanceRate',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'advanceRateReason',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'preAllocationLendableValue',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'postAllocationLendableValue',
    isVisible: true,
    isFixed: false,
  },
  // SEC Segment
  {
    columnKey: 'baseField-he_sec_segment-securities',
    isVisible: true,
    isFixed: false,
  },
  // Rating
  {
    columnKey: 'baseField-he_sec_rating-securities',
    isVisible: true,
    isFixed: false,
  },
  // Maturity
  {
    columnKey: 'baseField-he_sec_mat_dt-securities',
    isVisible: true,
    isFixed: false,
  },
  // Security Number
  {
    columnKey: 'baseField-he_sec_security_num-securities',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'collateralType',
    isVisible: true,
    isFixed: false,
  },
  // hide collateralDescription
  {
    columnKey: 'collateralDescription',
    isVisible: false,
    isFixed: false,
  },
  {
    columnKey: 'loanAccountNumber',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'heccrel_portfolio_number',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'he_sec_pldg_yn',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'currency_code',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'baseField-he_sec_cusip-securities',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'baseField-he_sec_strategy-securities',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'baseField-he_sec_asset_class-securities',
    isVisible: true,
    isFixed: false,
  },
  {
    columnKey: 'allocation',
    isVisible: false,
    isFixed: false,
  },
  {
    columnKey: 'baseField-he_sec_issuer_country-securities',
    isVisible: false,
    isFixed: false,
  },
];

const _BASE_DEFAULT_COLUMN_CONFIGS_ASSIGNMENT = _BASE_DEFAULT_COLUMN_CONFIGS_LOANS;

const _BASE_DEFAULT_COLUMN_CONFIGS_CUSTOMERS = _BASE_DEFAULT_COLUMN_CONFIGS_LOANS.filter(
  ({ columnKey }) => !IGNORED_CUSTOMER_COLUMN_SET.has(columnKey),
);

const _notInBaseListAssignment = ({ columnKey }: { columnKey: string }) =>
  !_BASE_DEFAULT_COLUMN_CONFIGS_ASSIGNMENT
    .map(({ columnKey: currColumnKey }) => currColumnKey)
    .includes(columnKey);

const _notInBaseListCustomer = ({ columnKey }: { columnKey: string }) =>
  !_BASE_DEFAULT_COLUMN_CONFIGS_CUSTOMERS
    .map(({ columnKey: currColumnKey }) => currColumnKey)
    .includes(columnKey);

const _notInBaseListLoans = ({ columnKey }: { columnKey: string }) =>
  !_BASE_DEFAULT_COLUMN_CONFIGS_LOANS
    .map(({ columnKey: currColumnKey }) => currColumnKey)
    .includes(columnKey);

const DEFAULT_TABLE_CONFIG_ASSIGNMENT = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY_ASSIGNMENT,
  columns: _.concat(
    _BASE_DEFAULT_COLUMN_CONFIGS_ASSIGNMENT,
    _BASE_DEFAULT_COLUMN_CONFIGS_BASEFIELDS.filter(_notInBaseListAssignment),
  ),
});

const DEFAULT_TABLE_CONFIG_CUSTOMER = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY_CUSTOMER,
  columns: _.concat(
    _BASE_DEFAULT_COLUMN_CONFIGS_CUSTOMERS,
    _BASE_DEFAULT_COLUMN_CONFIGS_BASEFIELDS.filter(_notInBaseListCustomer),
  ),
});

const DEFAULT_TABLE_CONFIG_LOANS = createDefaultTableColumnConfiguration({
  tableKey: TABLE_KEY_LOANS,
  columns: _.concat(
    _BASE_DEFAULT_COLUMN_CONFIGS_LOANS,
    _BASE_DEFAULT_COLUMN_CONFIGS_BASEFIELDS.filter(_notInBaseListLoans),
  ),
});

const FROZEN_COLUMN_IDS = [
  'accountNumber',
  'product_name',
  'baseField-he_sec_sec_desc-securities',
];

const normalizeColumnId = (columnId: string) => {
  if (columnId.startsWith('baseField-')) {
    return columnId.split('-')[1];
  }
  return columnId;
};

const isFrozenColumn = (columnId: string) => {
  const normalizedColumnId = normalizeColumnId(columnId);

  return FROZEN_COLUMN_IDS.includes(normalizedColumnId);
};

const getFrozenColumnWidthBeforeColumn = (
  columnId: string,
  columns: { id: string; width: number }[],
) => {
  const normalizedColumnId = normalizeColumnId(columnId);
  const index = FROZEN_COLUMN_IDS.indexOf(normalizedColumnId);
  if (index === -1) {
    return 0;
  }

  return columns.slice(0, index).reduce((acc, { width }) => acc + width, 0);
};

type CollateralTableBodyProps = {
  totalMarketValue: number;
  effectiveAllocationRate: number;
  totalPostAllocationLendableValue: number;
  effectiveAdvanceRate: number;
  totalPreAllocationLendableValue: number;
  collateral: Collateral[];
  columns: HoldingsTableColumnDef[];
  isDataLoading: boolean;
  isDataError: boolean;
  showAllocation: boolean;
  queuedSelectedFinleyReceivableIds?: Set<string> | null;
  toggleReceivableSelect?: (params: {
    finleyReceivableId: string;
    collateralAccountNumber: string;
    isChecked: boolean;
  }) => void;
  pageType: PageType;
};
const CollateralTableBody = ({
  collateral,
  columns,
  effectiveAllocationRate,
  totalPreAllocationLendableValue,
  totalMarketValue,
  totalPostAllocationLendableValue,
  effectiveAdvanceRate,
  isDataError,
  isDataLoading,
  showAllocation,
  queuedSelectedFinleyReceivableIds,
  pageType,
  toggleReceivableSelect,
}: CollateralTableBodyProps) => {
  const { loanAccountId } = useLoanAccountUrlParam();

  const { tableKey, defaultTableColumnConfiguration } = useMemo(() => {
    switch (pageType) {
      case 'assignment':
        return {
          tableKey: TABLE_KEY_ASSIGNMENT,
          defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG_ASSIGNMENT,
        };
      case 'loanAccounts':
        return {
          tableKey: TABLE_KEY_LOANS,
          defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG_LOANS,
        };
      case 'customers':
        return {
          tableKey: TABLE_KEY_CUSTOMER,
          defaultTableColumnConfiguration: DEFAULT_TABLE_CONFIG_CUSTOMER,
        };
      default:
        return finleyAssertUnreachable(pageType);
    }
  }, [pageType]);

  const {
    columnVisibility,
    columnOrder,
    columnConfiguration,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey,
    defaultTableColumnConfiguration,
  });

  const [sorting, setSorting] = useState<SortingState>([]);

  const showCheckboxColumn: boolean = useMemo(
    () => !!queuedSelectedFinleyReceivableIds && !!toggleReceivableSelect,
    [queuedSelectedFinleyReceivableIds, toggleReceivableSelect],
  );

  const table = useReactTable({
    data: collateral,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 100,
      },
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const frozenTableColumns = table
    .getHeaderGroups()[0]
    .headers.filter((header) => isFrozenColumn(header.column.id));
  const frozenTableColumnWidths = frozenTableColumns.map((header) => ({
    id: header.column.id,
    width: header.getSize() - 16,
  }));

  const tableDataForDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  return (
    <>
      <Flex w="100%" flexWrap="wrap" gridColumnGap="16px" gridRowGap="16px">
        <InfoCard
          title="Total Market Value"
          value={formatCurrencyAmount(totalMarketValue, Bank.Currency.USD)}
          icon={<Icon icon="dollar" size="24px" />}
          width="340px"
        />
        <InfoCard
          title="Blended Advance Rate"
          value={percentageToPercentageLabel(effectiveAdvanceRate * 100)}
          icon={<Icon icon="dollar" size="24px" />}
          width="340px"
        />
        <InfoCard
          title="Pre Allocation Lendable Value"
          value={formatCurrencyAmount(
            totalPreAllocationLendableValue,
            Bank.Currency.USD,
          )}
          icon={<Icon icon="dollar" size="24px" />}
          width="340px"
        />
        {showAllocation && (
          <>
            <InfoCard
              title="Effective Allocation"
              value={percentageToPercentageLabel(effectiveAllocationRate * 100)}
              icon={<Icon icon="dollar" size="24px" />}
              width="340px"
            />
            <InfoCard
              title="Post Allocation Lendable Value"
              value={formatCurrencyAmount(
                totalPostAllocationLendableValue,
                Bank.Currency.USD,
              )}
              icon={<Icon icon="dollar" size="24px" />}
              width="340px"
            />
          </>
        )}
      </Flex>
      <HStack sx={{ spacing: '1em' }} alignSelf="flex-end">
        <ColumnDisplaySettingsButton
          initialColumns={columnConfiguration}
          updateColumnConfigs={updateUserColumnConfiguration}
          columnNames={getColumnNames(columns)}
        />
        <CsvXlsxDownloadSelect
          dataRows={tableDataForDownload.dataRows}
          headers={tableDataForDownload.headers}
          filenamePrefix={`holdings-loanAccountNumber-${loanAccountId}`}
        />
      </HStack>

      <TableContainer
        mt="32px"
        borderWidth="medium"
        borderColor={colors.gray[200]}
        // maxH required for table top sticky header to work
        // since position:sticky attaches to the nearest ancestor with a scrolling mechanism
        // https://stackoverflow.com/questions/70770027/table-with-sticky-header-and-horizontal-scroll
        maxHeight="50vh"
        overflowX="auto"
        overflowY="auto"
        position="relative"
        id="receivables-dashboard-main-table"
      >
        <Table>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} style={frozenHeaderRowStyling}>
                {/* Checkbox column placeholder header cell */}
                {showCheckboxColumn && <Th />}
                {headerGroup.headers.map((header) => {
                  const columnCanSort = header.column.getCanSort();

                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      padding={
                        header.column.columnDef.meta?.style?.padding ||
                        '8px 16px 8px 16px'
                      }
                      fontSize="16px"
                      fontWeight="medium"
                      whiteSpace="nowrap"
                      color={colors.black[95]}
                      style={frozenMultipleHeaderCellStyling(
                        isFrozenColumn(header.column.id),
                        getFrozenColumnWidthBeforeColumn(
                          header.column.id,
                          frozenTableColumnWidths,
                        ),
                      )}
                      maxWidth="18em"
                    >
                      <Flex alignItems="center">
                        <Box
                          cursor={columnCanSort ? 'pointer' : 'default'}
                          onClick={header.column.getToggleSortingHandler()}
                          mr="8px"
                          color={colors.aurora[400]}
                          fontWeight="thin"
                        >
                          {{
                            asc: <Icon icon="sort-up" />,
                            desc: <Icon icon="sort-down" />,
                          }[header.column.getIsSorted() as string] ??
                            (columnCanSort ? <Icon icon="sort" /> : null)}
                        </Box>
                        <Box overflowWrap="break-word">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          {(isDataLoading || isDataError) && (
            <Tbody>
              {Array.from(Array(10).keys()).map((i) => (
                <Tr key={i}>
                  {columns.map((column) => (
                    <Td
                      key={`${column.id}+${i}`}
                      padding="8px 16px"
                      height="56px"
                    >
                      <Skeleton h="100%" />
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          )}
          {!isDataLoading && !isDataError && (
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                const finleyReceivableId =
                  row.original.receivable.finley_receivable_id;
                const collateralAccountNumber =
                  row.original.receivable.collateral_account_number;
                // account for cross collateralization, single receivable can show up multiple times
                const receivableKey = `${finleyReceivableId}-${row.original.receivable.loan_account_number}`;

                const isReceivableSelected = !!queuedSelectedFinleyReceivableIds?.has(
                  finleyReceivableId,
                );

                return (
                  <Tr key={receivableKey}>
                    {showCheckboxColumn && toggleReceivableSelect && (
                      <Td>
                        <Checkbox
                          defaultChecked={isReceivableSelected}
                          onChange={(e) => {
                            toggleReceivableSelect({
                              finleyReceivableId,
                              collateralAccountNumber,
                              isChecked: e.target.checked,
                            });
                          }}
                        />
                      </Td>
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={`${cell.id}+${receivableKey}`}
                        textAlign={cell.column.columnDef.meta?.style?.textAlign}
                        padding={
                          cell.column.columnDef.meta?.style?.padding || '16px'
                        }
                        height="56px"
                        style={frozenMultipleRowCellStyling(
                          isFrozenColumn(cell.column.id),
                          getFrozenColumnWidthBeforeColumn(
                            cell.column.id,
                            frozenTableColumnWidths,
                          ),
                        )}
                        maxWidth="3em"
                      >
                        <Box
                          sx={{ textWrap: 'wrap', overflowWrap: 'break-word' }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Box>
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          )}
        </Table>
      </TableContainer>
      <TanStackPaginationControl table={table} />
    </>
  );
};

const createBaseColumns = ({
  baseFieldColumnDefs,
  loanProductToName,
}: {
  baseFieldColumnDefs: HoldingsTableColumnDef[];
  loanProductToName: (loanProductId: string | null) => string;
}) => {
  const initColumns: HoldingsTableColumnDef[] = [
    // normalized base fields
    {
      id: 'collateralDescription',
      header: 'Collateral Description',
      accessorFn: ({ receivable }) => getCollateralDescription(receivable),
    },
    {
      id: 'accountNumber',
      header: 'Account Number',
      accessorFn: ({ account }) => account.accountNumber,
    },
    {
      id: 'product_name',
      header: 'Product Name',
      accessorFn: ({ receivable }) =>
        receivable.receivableReponseTapeRow.baseFields.product_name,
    },
    {
      id: 'collateralType',

      header: 'Collateral Type',
      accessorFn: ({ receivable }) =>
        loanProductToName(receivable.finley_loan_product_id),
    },
    // rtt base fields
    {
      id: 'loanAccountNumber',

      header: 'Loan Account',
      accessorFn: ({
        receivable: { loan_account_number: loanAccountNumber },
      }) => loanAccountNumber ?? 'Unassigned',
    },
    {
      id: 'marketValue',

      header: 'Market Value',
      accessorFn: ({ receivable }) => getPreAllocationMarketValue(receivable),
      cell: function CollateralMarketValue({ getValue }) {
        return formatCurrencyAmount(getValue(), Bank.Currency.USD);
      },
    },
    {
      id: 'advanceRate',

      header: 'Advance Rate',
      accessorFn: ({ receivable }) => getAdvanceRate(receivable),
      cell: function AdvanceRate({ getValue }) {
        return percentageToPercentageLabel(getValue() * 100, 2);
      },
    },
    {
      id: 'allocation',
      header: 'Allocation',
      accessorFn: ({ receivable }) => getAllocationRate(receivable),
      cell: function Allocation({ getValue }) {
        return percentageToPercentageLabel(getValue() * 100, 2);
      },
    },
    {
      id: 'postAllocationLendableValue',
      header: 'Post Allocation Lendable Value',
      accessorFn: ({ receivable }) =>
        getPostAllocationLendableValue(receivable),
      cell: function PostAllocationLendableValue({ getValue }) {
        return formatCurrencyAmount(getValue(), Bank.Currency.USD);
      },
    },
    {
      id: 'advanceRateReason',
      header: 'Advance Rate Reason',
      accessorFn: ({ receivable }) =>
        receivable.receivableReponseTapeRow.borrowingBaseFields.advanceRatePath,
      cell: function AdvanceRateReason({ getValue }) {
        const [
          reasonsString,
          ...modifiersString // array of potential modifier trees
        ] = getValue()?.split('>') ?? [];
        const reasons: string[] = reasonsString.split('#');
        const modifiers: string[][] =
          modifiersString?.map((modifierString: string) =>
            modifierString.split('#'),
          ) ?? [];

        if (reasons.length > 1 && reasons[0] === 'root') {
          reasons.shift();
        }
        return reasons.length > 0 ? (
          <Flex
            flexDirection="column"
            alignItems="flex-start"
            gridColumnGap="8px"
            gridRowGap="8px"
          >
            {reasons.map((level, index) => (
              // protecting against not unique reasons because they are not unique outside their level
              <HStack key={`${level}-${uuidV4()}`}>
                <Box
                  bgColor={colors.aurora[100]}
                  p="4px 16px"
                  borderRadius="8px"
                  color="white"
                  w="fit-content"
                >
                  {level === 'root' ? 'default' : level}
                </Box>
                {index < reasons.length - 1 && reasons.length > 0 && (
                  <Icon icon="chevron-right" />
                )}
              </HStack>
            ))}
            {modifiers.length > 0 && (
              <Tooltip
                label={
                  <Flex
                    w="300px"
                    padding="24px"
                    flexDirection="column"
                    gridRowGap="16px"
                  >
                    <Text>Modifiers</Text>
                    <Divider />
                    {modifiers.map((modifierTree) => (
                      <React.Fragment key={JSON.stringify(modifierTree)}>
                        <HStack
                          flexWrap="wrap"
                          key={uuidV4()}
                          spacing="8px"
                          w="100%"
                        >
                          {modifierTree.map((modifier, index) => (
                            <HStack key={uuidV4()}>
                              <Box
                                bgColor={colors.aurora[100]}
                                p="4px 16px"
                                borderRadius="8px"
                                color="white"
                                w="fit-content"
                              >
                                {modifier}
                              </Box>
                              {index < modifierTree.length - 1 &&
                                modifierTree.length > 0 && (
                                  <Icon icon="chevron-right" />
                                )}
                            </HStack>
                          ))}
                        </HStack>
                        <Divider />
                      </React.Fragment>
                    ))}
                  </Flex>
                }
              >
                <HStack p="4px 16px" borderRadius="8px" w="fit-content">
                  <Icon icon="arrow-turn-down-right" />
                  <Text>Modifiers</Text>
                </HStack>
              </Tooltip>
            )}
          </Flex>
        ) : (
          'No advance rate reason'
        );
      },
      enableSorting: false,
    },
    {
      id: 'preAllocationLendableValue',
      header: 'Pre Allocation Lendable Value',
      accessorFn: ({ receivable }) => getPreAllocationLendableValue(receivable),
      cell: function PreAllocationLendableValue({ getValue }) {
        return formatCurrencyAmount(getValue(), Bank.Currency.USD);
      },
    },
  ];

  const initColumnsWithMetadata: HoldingsTableColumnDef[] = initColumns.map(
    (column) => {
      const newColumnDef = _.cloneDeep(column);
      if (column.id) {
        newColumnDef.header = isBaseFieldToRename(_.snakeCase(column.id))
          ? getHeader(column.id)
          : column.header;
        newColumnDef.meta = {
          initialVisibility: isInitiallyVisible(column.id),
        };
      }
      return newColumnDef;
    },
  );

  const columns = _.concat(initColumnsWithMetadata, baseFieldColumnDefs);

  return {
    columns,
  };
};

type PageType = 'assignment' | 'customers' | 'loanAccounts';

type CollateralTableProps = {
  collateralAccounts: ValleyBankCollateralAccount[];
  isDataLoading: boolean;
  isDataError: boolean;
  showAllocation?: boolean;
  queuedSelectedFinleyReceivableIds?: Set<string> | null;
  toggleReceivableSelect?: (params: {
    finleyReceivableId: string;
    collateralAccountNumber: string;
    isChecked: boolean;
  }) => void;
  pageType: PageType;
};
export const CollateralTable = ({
  collateralAccounts,
  isDataLoading,
  isDataError,
  showAllocation = false,
  queuedSelectedFinleyReceivableIds,
  toggleReceivableSelect,
  pageType,
}: // if true, will remove columns that should not appear on customer page
CollateralTableProps) => {
  // TODO pass in isWip from DataMetadataContext
  const {
    columnDefs: baseFieldColumnDefs,
    isLoading: isBaseFieldConfigsLoading,
    loanProductToName,
  } = useBaseFieldColumnDefinitions();

  // @vb - @jen from vince - can I do this safely
  // review later
  const collateral: Collateral[] = useMemo(
    () =>
      Object.values(
        collateralAccounts
          .flatMap(({ receivables, ...account }) => {
            return receivables.map((receivable: Receivable) => ({
              account,
              receivable,
            }));
          })
          .reduce<{ [key: string]: Collateral }>((acc, collat) => {
            if (acc[collat.receivable.finley_receivable_id]) {
              // eslint-disable-next-line no-param-reassign
              acc[
                collat.receivable.finley_receivable_id
                // eslint-disable-next-line camelcase
              ].receivable.loan_account_number += `, ${collat.receivable.loan_account_number}`;
            } else {
              // eslint-disable-next-line no-param-reassign
              acc[collat.receivable.finley_receivable_id] = {
                ...collat,
                receivable: {
                  ...collat.receivable,
                },
              };
            }

            return acc;
          }, {}),
      ),
    [collateralAccounts],
  );

  const stats = getCollateralAccountStats(collateralAccounts);

  const { columns } = createBaseColumns({
    loanProductToName,
    baseFieldColumnDefs,
  });

  return (
    <>
      {!isDataLoading &&
        !isDataError &&
        isBaseFieldConfigsLoading &&
        collateral.length === 0 && (
          <ProgressAlert status="info">Loading holdings</ProgressAlert>
        )}
      {!isDataLoading &&
        !isDataError &&
        !isBaseFieldConfigsLoading &&
        collateral.length === 0 && (
          <Alert status="info">
            <AlertIcon />
            <AlertDescription>No Collateral found</AlertDescription>
          </Alert>
        )}
      {/* even if data error or loading, show skeleton to indicate table is loading */}
      {!isBaseFieldConfigsLoading && collateral.length > 0 && (
        <CollateralTableBody
          showAllocation={showAllocation}
          totalMarketValue={stats.totalMarketValue}
          effectiveAllocationRate={stats.effectiveAllocationRate}
          totalPostAllocationLendableValue={
            stats.totalPostAllocationLendableValue
          }
          effectiveAdvanceRate={stats.effectiveAdvanceRate}
          totalPreAllocationLendableValue={
            stats.totalPreAllocationLendableValue
          }
          collateral={collateral}
          columns={columns}
          isDataLoading={isDataLoading}
          isDataError={isDataError}
          queuedSelectedFinleyReceivableIds={queuedSelectedFinleyReceivableIds}
          toggleReceivableSelect={toggleReceivableSelect}
          pageType={pageType}
        />
      )}
    </>
  );
};
